// 設定画面に表示するモデルカタログ。
// 新しいモデルが出たらこのファイルにエントリを追加するだけでよい
// （プロバイダはモデルIDのプレフィックスから自動判定される。値の形式は "モデルID:思考指定"）。
// 一覧にないモデルは「ユーザー指定モデル」から直接IDを入力しても使える。

import type { Provider } from '../core/llm.js'

export interface ModelOption {
  value: string
  label: string
}

export interface ModelGroup {
  label: string
  /** 所属プロバイダ。未指定のグループ（ユーザー指定）は常に表示する */
  provider?: Provider
  options: ModelOption[]
}

/** プロバイダ切替時に選択する既定モデル */
export const DEFAULT_MODEL_BY_PROVIDER: Record<Provider, string> = {
  gemini: 'gemini-3.5-flash:medium',
  anthropic: 'claude-opus-4-8:high',
  openai: 'gpt-5.1:medium',
}

export const MODEL_CATALOG: ModelGroup[] = [
  {
    label: 'Gemini (Stable)',
    provider: 'gemini',
    options: [
      { value: 'gemini-3.5-flash:high', label: 'Gemini 3.5 Flash (Thinking High)' },
      { value: 'gemini-3.5-flash:medium', label: 'Gemini 3.5 Flash (Thinking Medium)' },
      { value: 'gemini-3.5-flash:low', label: 'Gemini 3.5 Flash (Thinking Low)' },
      { value: 'gemini-3.5-flash:minimal', label: 'Gemini 3.5 Flash (Thinking Minimal)' },
      { value: 'gemini-3.1-flash-lite:high', label: 'Gemini 3.1 Flash-Lite (Thinking High)' },
      { value: 'gemini-3.1-flash-lite:medium', label: 'Gemini 3.1 Flash-Lite (Thinking Medium)' },
      { value: 'gemini-3.1-flash-lite:low', label: 'Gemini 3.1 Flash-Lite (Thinking Low)' },
      { value: 'gemini-3.1-flash-lite:minimal', label: 'Gemini 3.1 Flash-Lite (Thinking Minimal)' },
      { value: 'gemini-2.5-pro:-1', label: 'Gemini 2.5 Pro (Thinking On)' },
      { value: 'gemini-2.5-flash:-1', label: 'Gemini 2.5 Flash (Thinking On)' },
      { value: 'gemini-2.5-flash:0', label: 'Gemini 2.5 Flash (Thinking Off)' },
      { value: 'gemini-2.5-flash-lite:-1', label: 'Gemini 2.5 Flash-Lite (Thinking On)' },
      { value: 'gemini-2.5-flash-lite:0', label: 'Gemini 2.5 Flash-Lite (Thinking Off)' },
    ],
  },
  {
    label: 'Gemini (Preview)',
    provider: 'gemini',
    options: [
      { value: 'gemini-3.1-pro-preview:high', label: 'Gemini 3.1 Pro (Thinking High)' },
      { value: 'gemini-3.1-pro-preview:medium', label: 'Gemini 3.1 Pro (Thinking Medium)' },
      { value: 'gemini-3.1-pro-preview:low', label: 'Gemini 3.1 Pro (Thinking Low)' },
      { value: 'gemini-3-flash-preview:high', label: 'Gemini 3 Flash (Thinking High)' },
      { value: 'gemini-3-flash-preview:medium', label: 'Gemini 3 Flash (Thinking Medium)' },
      { value: 'gemini-3-flash-preview:low', label: 'Gemini 3 Flash (Thinking Low)' },
      { value: 'gemini-3-flash-preview:minimal', label: 'Gemini 3 Flash (Thinking Minimal)' },
    ],
  },
  {
    label: 'Claude (Anthropic)',
    provider: 'anthropic',
    options: [
      { value: 'claude-opus-4-8:high', label: 'Claude Opus 4.8 (Effort High)' },
      { value: 'claude-opus-4-8:medium', label: 'Claude Opus 4.8 (Effort Medium)' },
      { value: 'claude-sonnet-5:high', label: 'Claude Sonnet 5 (Effort High)' },
      { value: 'claude-sonnet-5:medium', label: 'Claude Sonnet 5 (Effort Medium)' },
      { value: 'claude-sonnet-5:low', label: 'Claude Sonnet 5 (Effort Low)' },
      { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
    ],
  },
  {
    label: 'OpenAI',
    provider: 'openai',
    options: [
      { value: 'gpt-5.1:high', label: 'GPT-5.1 (Reasoning High)' },
      { value: 'gpt-5.1:medium', label: 'GPT-5.1 (Reasoning Medium)' },
      { value: 'gpt-5.1:low', label: 'GPT-5.1 (Reasoning Low)' },
      { value: 'gpt-5.1:none', label: 'GPT-5.1 (Reasoning None)' },
      { value: 'gpt-5-mini:medium', label: 'GPT-5 mini (Reasoning Medium)' },
      { value: 'gpt-5-mini:minimal', label: 'GPT-5 mini (Reasoning Minimal)' },
    ],
  },
  {
    label: 'ユーザー指定',
    options: [{ value: 'custom', label: 'ユーザー指定モデル' }],
  },
]

/** 設定画面の <select> に、指定プロバイダのモデル（+ユーザー指定）だけを流し込む */
export function populateModelSelect(select: HTMLSelectElement, provider: Provider): void {
  select.innerHTML = ''
  for (const group of MODEL_CATALOG) {
    if (group.provider !== undefined && group.provider !== provider) continue
    const optgroup = document.createElement('optgroup')
    optgroup.label = group.label
    for (const option of group.options) {
      const el = document.createElement('option')
      el.value = option.value
      el.textContent = option.label
      optgroup.appendChild(el)
    }
    select.appendChild(optgroup)
  }
}
